import React, { useState } from 'react';
import { motion } from 'framer-motion';
import cardTexture from '../assets/card_texture.jpg';

const MemberCard = ({
    userName = "GUEST",
    memberSinceDate = "11/23",
    tier = "gold", // 'gold' or 'executive'
    onLogout,
    onManageSubscription
}) => {
    const [isFlipped, setIsFlipped] = useState(false);

    const handleFlip = () => {
        setIsFlipped(!isFlipped);
    };

    const handleManage = (e) => {
        e.stopPropagation();
        if (onManageSubscription) onManageSubscription();
    };

    const isExecutive = tier === 'executive';

    // Styles
    const cardBackground = isExecutive
        ? 'linear-gradient(135deg, #1a1a1a, #000000)'
        : `linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 20%), linear-gradient(to bottom, #cfc09f, #a69279, #cfc09f)`;

    const textColor = isExecutive ? '#d4af37' : '#594e36';
    const labelColor = isExecutive ? '#8a7030' : '#7a6b50';

    const chipGradient = isExecutive
        ? 'linear-gradient(135deg, #d4af37, #aa882e)'
        : 'linear-gradient(135deg, #eacda3, #d6ae7b)';


    return (
        <div className="perspective-[1200px] w-[340px] h-[215px] cursor-pointer group relative max-w-full">
            <div className="w-full h-full sm:scale-100 scale-[0.85] origin-center transition-transform duration-300">
                <motion.div
                    className="w-full h-full relative preserve-3d"
                    initial={false}
                    animate={{
                        rotateY: isFlipped ? 180 : 0,
                        y: [0, -6, 0],
                        rotateX: [0, 2, 0, -2, 0],
                    }}
                    transition={{
                        rotateY: { duration: 0.6, type: "spring", stiffness: 260, damping: 20 },
                        y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                        rotateX: { duration: 6, repeat: Infinity, ease: "easeInOut" }
                    }}
                    whileHover={{
                        scale: 1.02,
                        rotateX: 5,
                        rotateY: isFlipped ? 185 : -5,
                        transition: { duration: 0.2 }
                    }}
                    onClick={handleFlip}
                    style={{ transformStyle: 'preserve-3d' }}
                >
                    {/* --- FRONT FACE --- */}
                    <div
                        className="absolute w-full h-full rounded-xl backface-hidden overflow-hidden shadow-2xl"
                        style={{
                            background: cardBackground,
                            border: isExecutive ? '1px solid #333' : 'none',
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden'
                        }}
                    >
                        {/* Custom Pattern Texture */}
                        <div className="absolute inset-0 bg-cover bg-center opacity-30 mix-blend-overlay"
                            style={{ backgroundImage: `url(${cardTexture})` }}
                        />

                        {/* Noise Texture */}
                        <div className="absolute inset-0 opacity-15 pointer-events-none mix-blend-multiply"
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                            }}
                        />

                        {/* Metallic Sheen */}
                        <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] opacity-30 pointer-events-none rotate-[25deg]"
                            style={{
                                background: 'linear-gradient(45deg, transparent 45%, rgba(255, 255, 255, 0.6) 50%, transparent 55%)'
                            }}
                        />

                        {/* Hologram */}
                        <div className="absolute top-4 left-4 w-[50px] h-[35px] rounded-md overflow-hidden z-10"
                            style={{
                                background: 'linear-gradient(135deg, #e6e6e6 20%, #ffffff 50%, #d4af37 80%)'
                            }}
                        >
                            <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] animate-spin-slow mix-blend-overlay"
                                style={{
                                    background: 'linear-gradient(45deg, rgba(255,0,0,0.2), rgba(255,255,0,0.2), rgba(0,255,0,0.2), rgba(0,255,255,0.2), rgba(0,0,255,0.2), rgba(255,0,255,0.2))',
                                    animation: 'spin 4s linear infinite'
                                }}
                            />
                        </div>

                        <div className="absolute top-4 right-4 text-right z-10">
                            <div className="font-sans text-[0.5rem] uppercase tracking-[0.15em] mb-[2px]" style={{ color: labelColor }}>STATUS</div>
                            <div className="font-mono text-xs font-bold" style={{ color: textColor }}>FULL CLEARANCE</div>
                        </div>

                        {/* Chip */}
                        <div className="absolute top-[40%] left-4 w-10 h-8 border rounded-sm opacity-80 z-10 flex items-center justify-center"
                            style={{
                                background: chipGradient,
                                borderColor: isExecutive ? '#8a7030' : '#a69279'
                            }}>
                            <div className="w-full h-full relative">
                                <div className="absolute top-1/2 left-0 w-full h-[1px] bg-black/20"></div>
                                <div className="absolute top-0 left-1/2 w-[1px] h-full bg-black/20"></div>
                                <div className="absolute top-1/2 left-1/2 w-4 h-4 border border-black/20 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
                            </div>
                        </div>

                        <div className="absolute top-[40%] left-0 w-full text-center px-4 z-10">
                            <h2 className="font-mono text-xl tracking-widest truncate uppercase"
                                style={{
                                    color: textColor,
                                    fontFamily: '"OCR A Std", "Courier New", monospace',
                                    textShadow: isExecutive ? '0 1px 2px rgba(0,0,0,0.8)' : '1px 1px 0px rgba(255,255,255,0.7), -1px -1px 0px rgba(0,0,0,0.2)'
                                }}>
                                {userName}
                            </h2>
                        </div>

                        <div className="absolute bottom-4 left-4 z-10">
                            <div className="font-sans text-[0.5rem] uppercase tracking-[0.15em] mb-[2px]" style={{ color: labelColor }}>ISSUED</div>
                            <div className="font-mono text-sm font-bold tracking-wider" style={{ color: textColor }}>
                                {memberSinceDate.includes('/') ? memberSinceDate : '11/23'}
                            </div>
                        </div>

                        <div className="absolute bottom-4 right-4 z-10">
                            <div className="font-mono text-[10px] opacity-70" style={{ color: textColor }}>
                                {isExecutive ? 'EXECUTIVE // LEVEL 9' : 'LEVEL 5'}
                            </div>
                        </div>
                    </div>

                    {/* --- BACK FACE --- */}
                    <div
                        className="absolute w-full h-full rounded-xl backface-hidden flex flex-col justify-center overflow-hidden shadow-2xl bg-[#1a1a1a]"
                        style={{
                            transform: 'rotateY(180deg)',
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden',
                            backgroundImage: 'radial-gradient(circle at 50% 50%, #2a2a2a 0%, #1a1a1a 100%)',
                            border: isExecutive ? '1px solid #333' : 'none'
                        }}
                    >
                        {/* Magnetic Strip */}
                        <div
                            className="w-full h-12 bg-black absolute top-[25px] flex items-center justify-end pr-5"
                        >
                            {/* Texture */}
                            <div className="absolute inset-0"
                                style={{
                                    background: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(50,50,50,0.5) 2px, rgba(50,50,50,0.5) 4px)'
                                }}
                            />
                            <span className="font-mono text-[10px] tracking-[0.2em] text-[#444] z-10" >
                                TERMINATE_SESSION
                            </span>
                        </div>

                        {/* Signature Panel */}
                        <div
                            className="w-[80%] h-10 bg-[#e5e7eb] mx-auto mt-[90px] rounded-sm cursor-pointer relative flex items-center px-[10px] hover:bg-white hover:shadow-[0_0_10px_rgba(74,222,128,0.3)] transition-all"
                            onClick={handleManage}
                        >
                            <div className="font-serif text-gray-500 text-sm italic">
                                {userName.substring(0, 15)}...
                            </div>
                            <div className="absolute right-[-30px] top-1/2 -translate-y-1/2 bg-white px-1 py-0.5 font-mono text-[10px] text-black rounded-sm">
                                999
                            </div>
                            <span className="absolute right-[10px] font-mono text-[10px] text-[#333] tracking-widest opacity-50 pointer-events-none">
                                MANAGE SUBSCRIPTION
                            </span>
                        </div>

                        <div className="absolute bottom-4 w-full text-center">
                            <div className="text-[6px] text-gray-600 tracking-widest">
                                PROPERTY OF PARADISE SYSTEMS CORP.
                            </div>
                            <div className="text-[6px] text-gray-600 tracking-widest mt-1">
                                IF FOUND RETURN TO NEAREST TERMINAL
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default MemberCard;
